"use client";

import { useEffect } from "react";
import { useActionState } from "react";
import { signIn } from "next-auth/react";
import { useRouter } from "next/navigation";
import { signUpAction, type SignUpState } from "./actions";

const initialState: SignUpState = {};

export function SignUpForm() {
  const router = useRouter();
  const [state, formAction, pending] = useActionState(signUpAction, initialState);

  useEffect(() => {
    async function autoLogin() {
      if (!state.success) return;

      const form = document.getElementById("signup-form") as HTMLFormElement | null;
      if (!form) return;

      const email = (form.elements.namedItem("email") as HTMLInputElement)?.value;
      const password = (form.elements.namedItem("password") as HTMLInputElement)?.value;

      if (!email || !password) return;

      const result = await signIn("credentials", {
        email,
        password,
        redirect: false,
      });

      if (result?.ok) {
        router.push("/compte");
        router.refresh();
      }
    }

    autoLogin();
  }, [state.success, router]);

  return (
    <form
      id="signup-form"
      action={formAction}
      className="rounded-[30px] border border-[var(--border)] bg-[linear-gradient(145deg,rgba(209,15,115,0.14),rgba(217,90,47,0.10))] p-6 shadow-[var(--shadow)] sm:p-8"
    >
      <div className="grid gap-5 sm:grid-cols-2">
        <div>
          <label
            htmlFor="firstName"
            className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
          >
            Prénom
          </label>
          <input
            id="firstName"
            name="firstName"
            type="text"
            required
            className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
          />
        </div>

        <div>
          <label
            htmlFor="lastName"
            className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
          >
            Nom
          </label>
          <input
            id="lastName"
            name="lastName"
            type="text"
            required
            className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
          />
        </div>
      </div>

      <div className="mt-5">
        <label
          htmlFor="email"
          className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
        >
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          required
          className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
        />
      </div>

      <div className="mt-5">
        <label
          htmlFor="phone"
          className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
        >
          Téléphone
        </label>
        <input
          id="phone"
          name="phone"
          type="tel"
          required
          className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
        />
      </div>

      <div className="mt-5">
        <label
          htmlFor="address1"
          className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
        >
          Adresse
        </label>
        <input
          id="address1"
          name="address1"
          type="text"
          required
          className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
        />
      </div>

      <div className="mt-5">
        <label
          htmlFor="address2"
          className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
        >
          Complément d’adresse <span className="text-[var(--foreground)]/45">(optionnel)</span>
        </label>
        <input
          id="address2"
          name="address2"
          type="text"
          className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
        />
      </div>

      <div className="mt-5 grid gap-5 sm:grid-cols-3">
        <div>
          <label
            htmlFor="postalCode"
            className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
          >
            Code postal
          </label>
          <input
            id="postalCode"
            name="postalCode"
            type="text"
            required
            className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
          />
        </div>

        <div className="sm:col-span-2">
          <label
            htmlFor="city"
            className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
          >
            Ville
          </label>
          <input
            id="city"
            name="city"
            type="text"
            required
            className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
          />
        </div>
      </div>

      <div className="mt-5">
        <label
          htmlFor="country"
          className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
        >
          Pays
        </label>
        <input
          id="country"
          name="country"
          type="text"
          defaultValue="France"
          required
          className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
        />
      </div>

      <div className="mt-5">
        <label
          htmlFor="password"
          className="mb-2 block text-sm font-medium text-[var(--foreground)]/80"
        >
          Mot de passe
        </label>
        <input
          id="password"
          name="password"
          type="password"
          required
          className="w-full rounded-2xl border border-[var(--border)] bg-[var(--card)] px-4 py-3 outline-none transition focus:border-[var(--primary)]"
        />
      </div>

      {state.error ? (
        <p className="mt-4 text-sm text-red-600">{state.error}</p>
      ) : null}

      {state.success ? (
        <p className="mt-4 text-sm text-green-700">
          Compte créé, connexion en cours...
        </p>
      ) : null}

      <button
        type="submit"
        disabled={pending}
        className="mt-6 cursor-pointer rounded-full bg-[var(--primary)] px-6 py-3 text-sm font-medium text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
      >
        {pending ? "Création..." : "Créer mon compte"}
      </button>
    </form>
  );
}